import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import { DEVTOOLS_OPTIONS } from '@/modules/debug/constants/zustand';

import { Collision } from '../types';

type State = {
  collisionState: Map<string, string[]>;
};

const INITIAL_STATE: State = {
  collisionState: new Map(),
};

type Action = {
  /**
   * @description Adds and removes collisions (both directions) in one store write. 
   */
  apply(collisionsToAdd: Collision[], collisionsToRemove: Collision[]): void;
  reset(): void;
};

export const useCollisionStore = create<State & Action>()(
  devtools(
    (set) => ({
      ...INITIAL_STATE,

      apply(collisionsToAdd: Collision[], collisionsToRemove: Collision[]) {
        if (collisionsToAdd.length === 0 && collisionsToRemove.length === 0) return;

        set(
          (state) => {
            const changedIds = new Map<string, Set<string>>();
            const getIds = (id: string): Set<string> => {
              const existing = changedIds.get(id);
              if (existing) return existing;
              const ids = new Set(state.collisionState.get(id));
              changedIds.set(id, ids);
              return ids;
            };

            collisionsToAdd.forEach(({ idA, idB }) => {
              getIds(idA).add(idB);
              getIds(idB).add(idA);
            });
            collisionsToRemove.forEach(({ idA, idB }) => {
              getIds(idA).delete(idB);
              getIds(idB).delete(idA);
            });

            const collisionState = new Map(state.collisionState);
            changedIds.forEach((ids, id) => {
              if (ids.size === 0) collisionState.delete(id);
              else collisionState.set(id, [...ids]);
            });

            return { collisionState };
          },
          undefined,
          {
            type: 'apply',
          },
        );
      },

      reset() {
        set(INITIAL_STATE, undefined, {
          type: 'reset',
        });
      },
    }),
    { store: 'collisionStore', ...DEVTOOLS_OPTIONS },
  ),
);
